import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, FileText, Link as LinkIcon, Cloud, TrendingUp, Calendar } from 'lucide-react'
import { fetchStats, type Statistics } from '../utils/api'

const ACTION_LABELS: Record<string, string> = {
  pdf_generated: 'PDFs Gerados',
  url_generated: 'Links Gerados',
  cloud_upload: 'Uploads Cloud',
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  pdf_generated: <FileText className="w-5 h-5" />,
  url_generated: <LinkIcon className="w-5 h-5" />,
  cloud_upload: <Cloud className="w-5 h-5" />,
}

const ACTION_COLORS: Record<string, string> = {
  pdf_generated: 'bg-blue-100 text-blue-600',
  url_generated: 'bg-purple-100 text-purple-600',
  cloud_upload: 'bg-green-100 text-green-600',
}

export function AdminStatsPage() {
  const [stats, setStats] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState(30)

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchStats(days)
      .then(setStats)
      .catch((err) => {
        console.error('Stats error:', err)
        setError(err.message || 'Erro ao carregar estatísticas')
      })
      .finally(() => setLoading(false))
  }, [days])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">{error || 'Erro ao carregar estatísticas'}</p>
        </div>
      </div>
    )
  }

  // Group daily stats by date
  const dailyByDate = stats.daily.reduce((acc, item) => {
    if (!acc[item.date]) acc[item.date] = {}
    acc[item.date][item.action] = item.total
    return acc
  }, {} as Record<string, Record<string, number>>)

  // Get unique dates sorted descending
  const dates = Object.keys(dailyByDate).sort((a, b) => b.localeCompare(a)).slice(0, 14)

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Estatísticas</h1>
              <p className="text-sm text-gray-500">Dados anónimos de utilização</p>
            </div>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="input w-auto"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
          </select>
        </div>

        {/* Today's Stats */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-600" />
            Hoje
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(ACTION_LABELS).map(([action, label]) => (
              <div key={action} className="text-center p-4 bg-gray-50 rounded-xl">
                <div className={`w-10 h-10 rounded-full ${ACTION_COLORS[action]} flex items-center justify-center mx-auto mb-2`}>
                  {ACTION_ICONS[action]}
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.today[action] || 0}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Period Totals */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-600" />
            Total ({days} dias)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(ACTION_LABELS).map(([action, label]) => (
              <div key={action} className="text-center p-4 bg-gray-50 rounded-xl">
                <div className={`w-10 h-10 rounded-full ${ACTION_COLORS[action]} flex items-center justify-center mx-auto mb-2`}>
                  {ACTION_ICONS[action]}
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.totals[action] || 0}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Breakdown */}
        {dates.length > 0 && (
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Últimos {Math.min(14, dates.length)} dias</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Data</th>
                    {Object.entries(ACTION_LABELS).map(([action, label]) => (
                      <th key={action} className="text-center py-2 px-3 font-medium text-gray-600">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dates.map((date) => (
                    <tr key={date} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-900">
                        {new Date(date).toLocaleDateString('pt-PT', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </td>
                      {Object.keys(ACTION_LABELS).map((action) => (
                        <td key={action} className="text-center py-2 px-3 text-gray-700">
                          {dailyByDate[date]?.[action] || 0}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Per Template Stats */}
        {stats.by_template.length > 0 && (
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Por Modelo</h2>
            <div className="space-y-3">
              {stats.by_template.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full ${ACTION_COLORS[item.action]} flex items-center justify-center`}>
                      {ACTION_ICONS[item.action]}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{item.template_name || 'Modelo removido'}</p>
                      <p className="text-xs text-gray-500">{ACTION_LABELS[item.action]}</p>
                    </div>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">{item.total}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-center text-gray-400">
          Estas estatísticas são completamente anónimas e não contêm dados pessoais identificáveis.
        </p>
      </div>
    </div>
  )
}
